import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Column } from "../../../../core/components/Column";
import { Container } from "../../../../core/components/Container";
import { CustomButton } from "../../../../core/components/CustomButton";
import { TextComponent } from "../../../../core/components/TextComponent";
import { RootState } from "../../../../core/store/store";
import {
	apiServices,
	getApiErrorMessage,
} from "../../../../core/utilities/ApiServices";
import {
	affiliateAllowedCountries,
	isAffiliateCountryAllowed,
	normalizeAffiliateCountry,
} from "../utils/affiliateCountries";
import {
	AffiliateDashboardWorkspace,
	normalizeAffiliateSection,
} from "./AffiliateDashboardWorkspace";
import type { AffiliateSupportDetails } from "./AffiliateDashboardWorkspace";

interface AffiliateStats {
	clicks: number;
	signups: number;
	paidUsers: number;
	grossTrackedAmount: number;
	pendingEarnings: number;
	payableEarnings: number;
	paidEarnings: number;
	blockedEarnings: number;
}

interface AffiliateCreative {
	id: string;
	title: string;
	dimensions: string;
	format: string;
	previewUrl: string | null;
	downloadUrl: string;
	sourceUrl: string;
	notes: string;
}

interface PerformancePoint {
	date?: string;
	label?: string;
	clicks: number;
	signups: number;
	paidUsers: number;
	conversionRate: number;
	earnings: number;
}

interface AffiliateInfo {
	joined: boolean;
	status: string;
	payoutProvider: string;
	payoutSetupStatus: string;
	payoutSetupMessage: string;
	payoutSetupAction: string;
	code: string;
	promoCode: string;
	link: string;
	assignedCode?: string;
	assignedLink?: string;
	customCodeRequest?: {
		id: string;
		code: string;
		status: "auto_approved" | "needs_review" | "admin_approved" | "rejected";
		active: boolean;
		reasons: string[];
		requestedAt: string | null;
		reviewedAt: string | null;
	} | null;
	disclosureText: string;
	termsSummary: string;
	termsVersion: string;
	legalReviewNotice: string;
	profile: {
		fullName: string;
		businessName: string;
		displayName: string;
		contactEmail: string;
		country: string;
		taxCountry: string;
		websiteUrl: string;
		primaryChannelUrl: string;
		audienceSizeRange: string;
		promotionPlan: string;
		creatorChannelUrl: string;
		referralSource: string;
		payoutMethodPreference: string;
		payoutPayPalEmail: string;
	};
	stats: AffiliateStats;
	nextPayoutDate: string | null;
	minimumPayoutProgress: {
		minimumAmount: number;
		payableAmount: number;
		percent: number;
		remainingAmount: number;
	};
	referralJourney: {
		clickedLink: number;
		signedUp: number;
		subscribed: number;
		eligibleCommission: number;
	};
	performance: {
		daily: PerformancePoint[];
		weekly: PerformancePoint[];
		monthly: PerformancePoint[];
	};
	trafficSources: Array<{
		source: string;
		clicks: number;
		signups: number;
		paidUsers: number;
		conversionRate: number;
		earnings: number;
	}>;
	campaignLinks: Array<{
		id: string;
		label: string;
		source: string;
		link: string;
		clicks: number;
		signups: number;
		paidUsers: number;
		earnings: number;
		createdAt: string | null;
	}>;
	marketingKit: {
		banners: AffiliateCreative[];
		captions: string[];
		emailCopy: string[];
		disclosureCopy: string[];
		videoScripts: string[];
		videoAds: Array<{
			id: string;
			title: string;
			duration: string;
			videoUrl: string;
			thumbnailUrl: string;
			suggestedCaption: string;
		}>;
		postingRules: string[];
	};
	payoutHistory: Array<{
		id: string;
		amount: number;
		currency: string;
		status: string;
		provider: string;
		failureReason: string;
		paidAt: string | null;
		createdAt: string | null;
	}>;
	commissionRules: Array<{
		title: string;
		description: string;
	}>;
	notifications: Array<{
		id: string;
		title: string;
		message: string;
		createdAt: string | null;
		tone: "success" | "warning" | "info";
	}>;
	tierProgress: {
		currentTier: string;
		nextTier: string;
		paidUsers: number;
		targetPaidUsers: number;
		percent: number;
	};
	settings: {
		waitingDays: number;
		minimumPayoutAmount: number;
		commissionRates: number[];
		currency: string;
		automaticPayoutsEnabled: boolean;
	};
	creatives?: AffiliateCreative[];
}

type JoinForm = {
	fullName: string;
	businessName: string;
	displayName: string;
	contactEmail: string;
	country: string;
	taxCountry: string;
	websiteUrl: string;
	primaryChannelUrl: string;
	audienceSizeRange: string;
	promotionPlan: string;
	creatorChannelUrl: string;
	referralSource: string;
	referralSourceOther: string;
	payoutMethodPreference: string;
	payoutPayPalEmail: string;
	acceptedTerms: boolean;
	acceptedDisclosure: boolean;
	acceptedPrivacy: boolean;
	acceptedPayoutRules: boolean;
};

const emptyStats: AffiliateStats = {
	clicks: 0,
	signups: 0,
	paidUsers: 0,
	grossTrackedAmount: 0,
	pendingEarnings: 0,
	payableEarnings: 0,
	paidEarnings: 0,
	blockedEarnings: 0,
};

const money = (value: number) =>
	Number(value || 0).toLocaleString("en-AU", {
		style: "currency",
		currency: "AUD",
	});

const statItems = (stats: AffiliateStats) => [
	["Clicks", stats.clicks.toLocaleString("en-AU")],
	["Signups", stats.signups.toLocaleString("en-AU")],
	["Paid members", stats.paidUsers.toLocaleString("en-AU")],
	["Tracked sales", money(stats.grossTrackedAmount)],
	["Pending", money(stats.pendingEarnings)],
	["Payable", money(stats.payableEarnings)],
	["Paid", money(stats.paidEarnings)],
	["Blocked", money(stats.blockedEarnings)],
];

const affiliateNavItems = [
	["overview", "Overview"],
	["application", "Application"],
	["journey", "Journey"],
	["performance", "Performance"],
	["links", "Links"],
	["kit", "Marketing kit"],
	["payouts", "Payouts"],
	["rules", "Rules"],
	["support", "Support"],
	["disclosure", "Disclosure"],
];

const conversionRate = (stats: AffiliateStats) =>
	stats.clicks > 0 ? Math.round((stats.signups / stats.clicks) * 100) : 0;

const latestPoint = (rows: PerformancePoint[]) =>
	rows.length > 0
		? rows[rows.length - 1]
		: { clicks: 0, signups: 0, paidUsers: 0, conversionRate: 0, earnings: 0 };

const numberText = (value: number) =>
	Number(value || 0).toLocaleString("en-AU");

const cleanLabel = (value: string) =>
	String(value || "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (letter) => letter.toUpperCase());

const dateText = (value: string | null) =>
	value
		? new Date(value).toLocaleDateString("en-AU", {
				day: "2-digit",
				month: "short",
				year: "numeric",
			})
		: "Not scheduled yet";

const MiniStat = ({ label, value }: { label: string; value: string }) => (
	<div className="border border-slate-800 bg-slate-900/80 p-4">
		<div className="text-xs font-semibold uppercase tracking-normal text-slate-500">
			{label}
		</div>
		<div className="mt-2 text-xl font-semibold text-slate-50">{value}</div>
	</div>
);

const ProgressBar = ({ percent }: { percent: number }) => (
	<div className="h-2 overflow-hidden bg-slate-800">
		<div
			className="h-full bg-pink-400"
			style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
		/>
	</div>
);

const SparkBars = ({ rows }: { rows: PerformancePoint[] }) => {
	const safeRows =
		rows.length > 0
			? rows.slice(-18)
			: [
					{
						clicks: 0,
						signups: 0,
						paidUsers: 0,
						conversionRate: 0,
						earnings: 0,
					},
				];
	const max = Math.max(...safeRows.map((row) => row.clicks), 1);
	return (
		<div className="flex h-28 items-end gap-1">
			{safeRows.map((row, index) => (
				<div
					key={`${row.date || row.label || index}-${index}`}
					className="min-w-0 flex-1 bg-pink-400/80"
					style={{ height: `${Math.max(6, (row.clicks / max) * 100)}%` }}
					title={`${row.date || row.label || "Day"}: ${row.clicks} clicks`}
				/>
			))}
		</div>
	);
};

const agreementItems = [
	"Affiliate access is separate from normal dating membership approval and may be reviewed, held, declined, or removed by Unjabbed.",
	"Commissions are limited to the first three eligible paid subscription payments from each referred member, at the current affiliate rates shown in the app.",
	"No commission is owed for free accounts, admin-created accounts, self-referrals, fake referrals, refunds, reversals, chargebacks, fraud, unsafe promotion, or policy breaches.",
	"Affiliates must clearly disclose that they may earn a commission when sharing their affiliate link.",
	"Affiliates must not use spam, misleading claims, fake profiles, fake testimonials, harassment, adult or hateful content, or unsafe promotion methods.",
	"Unjabbed may use affiliate application details, tracking details, payout details, fraud review notes, and PayPal payout email details to run the affiliate program.",
	"Payouts are PayPal-first. Unjabbed does not collect bank details for affiliate payouts. Real payouts can be held until minimum payout, safety, refund, reversal, chargeback, fraud, and account checks pass.",
];

const referralSourceOptions = [
	"Unjabbed website",
	"Unjabbed dating app",
	"Email from Unjabbed",
	"Friend or family",
	"Existing Unjabbed member",
	"Google search",
	"Facebook",
	"Instagram",
	"X",
	"YouTube",
	"TikTok",
	"Telegram",
	"Reddit",
	"Podcast",
	"Newsletter",
	"Blog or article",
	"Online community or forum",
	"Event or meetup",
	"Creator or influencer",
	"News or media",
	"App Store or Google Play",
	"Other",
];

const audienceSizeOptions = [
	"Under 1,000",
	"1,000 to 10,000",
	"10,000 to 50,000",
	"50,000 to 250,000",
	"250,000+",
];

const campaignSourceOptions = [
	"YouTube",
	"Facebook",
	"Email",
	"Website",
	"Podcast",
	"Telegram",
	"X",
	"Other",
];

export const AffiliateScreen = () => {
	const location = useLocation();
	const { section } = useParams();
	const { user } = useSelector((state: RootState) => state.onboarding);
	const [affiliate, setAffiliate] = useState<AffiliateInfo | null>(null);
	const memberCountry = normalizeAffiliateCountry(user?.country);
	const canJoinFromMemberCountry = isAffiliateCountryAllowed(user?.country);
	const [form, setForm] = useState<JoinForm>({
		fullName: "",
		businessName: "",
		displayName: "",
		contactEmail: "",
		country: "",
		taxCountry: "",
		websiteUrl: "",
		primaryChannelUrl: "",
		audienceSizeRange: "",
		promotionPlan: "",
		creatorChannelUrl: "",
		referralSource: "",
		referralSourceOther: "",
		payoutMethodPreference: "PayPal Payouts",
		payoutPayPalEmail: "",
		acceptedTerms: false,
		acceptedDisclosure: false,
		acceptedPrivacy: false,
		acceptedPayoutRules: false,
	});
	const [loading, setLoading] = useState(true);
	const [joining, setJoining] = useState(false);
	const [payoutLoading, setPayoutLoading] = useState(false);
	const [payoutEmailSaving, setPayoutEmailSaving] = useState(false);
	const [campaignSaving, setCampaignSaving] = useState(false);
	const [supportSaving, setSupportSaving] = useState(false);
	const [payoutMessage, setPayoutMessage] = useState("");
	const [payoutEmailMessage, setPayoutEmailMessage] = useState("");
	const [campaignMessage, setCampaignMessage] = useState("");
	const [supportRequestMessage, setSupportRequestMessage] = useState("");
	const [campaignLabel, setCampaignLabel] = useState("");
	const [campaignSource, setCampaignSource] = useState("YouTube");
	const [customCode, setCustomCode] = useState("");
	const [supportSubject, setSupportSubject] = useState("");
	const [supportMessage, setSupportMessage] = useState("");
	const [copied, setCopied] = useState(false);
	const [error, setError] = useState("");
	const [showAgreement, setShowAgreement] = useState(false);
	const [agreementScrolled, setAgreementScrolled] = useState(false);
	const agreementRef = useRef<HTMLDivElement | null>(null);

	const loadAffiliate = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const response = await apiServices.get("/affiliate/me");
			setAffiliate(response.data);
			const profile = response.data?.profile || {};
			const profileCountry = normalizeAffiliateCountry(profile.country);
			const profileTaxCountry = normalizeAffiliateCountry(profile.taxCountry);
			const savedReferralSource = String(profile.referralSource || "");
			setForm((current) => ({
				...current,
				fullName: profile.fullName || current.fullName,
				businessName: profile.businessName || current.businessName,
				displayName: profile.displayName || current.displayName,
				contactEmail:
					profile.contactEmail || current.contactEmail || user?.email || "",
				country: profileCountry || current.country || memberCountry,
				taxCountry: profileTaxCountry || current.taxCountry || memberCountry,
				websiteUrl: profile.websiteUrl || current.websiteUrl,
				primaryChannelUrl:
					profile.primaryChannelUrl ||
					profile.creatorChannelUrl ||
					current.primaryChannelUrl,
				audienceSizeRange:
					profile.audienceSizeRange || current.audienceSizeRange,
				promotionPlan: profile.promotionPlan || current.promotionPlan,
				creatorChannelUrl:
					profile.creatorChannelUrl || current.creatorChannelUrl,
				referralSource:
					referralSourceOptions.includes(savedReferralSource) &&
					savedReferralSource !== "Other"
						? savedReferralSource
						: current.referralSource,
				referralSourceOther:
					savedReferralSource &&
					!referralSourceOptions.includes(savedReferralSource)
						? savedReferralSource.replace(/^Other:\s*/i, "")
						: current.referralSourceOther,
				payoutMethodPreference:
					profile.payoutMethodPreference || current.payoutMethodPreference,
				payoutPayPalEmail:
					profile.payoutPayPalEmail || current.payoutPayPalEmail,
			}));
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not load affiliate details."));
		} finally {
			setLoading(false);
		}
	}, [memberCountry, user?.email]);

	useEffect(() => {
		loadAffiliate();
	}, [loadAffiliate]);

	useEffect(() => {
		if (!memberCountry) return;
		setForm((current) => ({
			...current,
			country: current.country || memberCountry,
			taxCountry: current.taxCountry || memberCountry,
		}));
	}, [memberCountry]);

	const validateJoinForm = () => {
		if (!canJoinFromMemberCountry) {
			setError("The affiliate program is not available in your country yet.");
			return false;
		}
		if (!form.fullName.trim()) {
			setError("Please add your full legal name.");
			return false;
		}
		if (!form.displayName.trim()) {
			setError("Please add your public affiliate display name.");
			return false;
		}
		if (!form.contactEmail.includes("@")) {
			setError("Please add a valid contact email.");
			return false;
		}
		if (!form.country) {
			setError("Please select your affiliate country.");
			return false;
		}
		if (!form.taxCountry) {
			setError("Please select your tax country.");
			return false;
		}
		if (!form.primaryChannelUrl.trim()) {
			setError(
				"Please add your main social, newsletter, podcast, community, or website link.",
			);
			return false;
		}
		if (!form.audienceSizeRange) {
			setError("Please select your audience size.");
			return false;
		}
		if (!form.promotionPlan.trim()) {
			setError("Please explain how you will promote Unjabbed.");
			return false;
		}
		if (!form.payoutPayPalEmail.includes("@")) {
			setError("Please add a valid PayPal payout email.");
			return false;
		}
		if (!form.referralSource) {
			setError("Please select how you heard about the affiliate program.");
			return false;
		}
		if (form.referralSource === "Other" && !form.referralSourceOther.trim()) {
			setError("Please tell us where you heard about the affiliate program.");
			return false;
		}
		return true;
	};

	const openAgreement = () => {
		if (!validateJoinForm()) return;
		setError("");
		setAgreementScrolled(false);
		setShowAgreement(true);
		window.setTimeout(() => {
			if (agreementRef.current) {
				agreementRef.current.scrollTop = 0;
			}
		}, 0);
	};

	const handleAgreementScroll = () => {
		const box = agreementRef.current;
		if (!box) return;
		const isAtBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 8;
		if (isAtBottom) setAgreementScrolled(true);
	};

	const joinProgram = async () => {
		if (!validateJoinForm()) return;
		const acceptedForm: JoinForm = {
			...form,
			creatorChannelUrl: form.primaryChannelUrl,
			referralSource:
				form.referralSource === "Other"
					? `Other: ${form.referralSourceOther.trim()}`
					: form.referralSource,
			acceptedTerms: true,
			acceptedDisclosure: true,
			acceptedPrivacy: true,
			acceptedPayoutRules: true,
		};
		setJoining(true);
		setError("");
		try {
			const response = await apiServices.post("/affiliate/join", acceptedForm);
			setForm(acceptedForm);
			setAffiliate(response.data);
			setShowAgreement(false);
		} catch (err) {
			setError(
				getApiErrorMessage(err, "Could not join the affiliate program."),
			);
		} finally {
			setJoining(false);
		}
	};

	const startPayoutSetup = async () => {
		setPayoutLoading(true);
		setPayoutMessage("");
		setPayoutEmailMessage("");
		setCampaignMessage("");
		setSupportRequestMessage("");
		setError("");
		try {
			const response = await apiServices.post("/affiliate/payout-setup/start");
			if (response.data?.url) {
				window.location.assign(response.data.url);
				return;
			}
			setPayoutMessage(
				response.data?.message ||
					"Payout setup is not available yet. Earnings will stay pending until setup is ready.",
			);
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not start payout setup."));
		} finally {
			setPayoutLoading(false);
		}
	};

	const savePayoutEmail = async () => {
		if (!form.payoutPayPalEmail.includes("@")) {
			setError("Please add a valid PayPal payout email.");
			return;
		}
		setPayoutEmailSaving(true);
		setPayoutEmailMessage("");
		setPayoutMessage("");
		setCampaignMessage("");
		setSupportRequestMessage("");
		setError("");
		try {
			const response = await apiServices.post(
				"/affiliate/payout-setup/paypal-email",
				{ payoutPayPalEmail: form.payoutPayPalEmail },
			);
			setAffiliate(response.data);
			setForm((current) => ({
				...current,
				payoutPayPalEmail:
					response.data?.profile?.payoutPayPalEmail ||
					current.payoutPayPalEmail,
			}));
			setPayoutEmailMessage("PayPal payout email saved.");
		} catch (err) {
			setError(
				getApiErrorMessage(err, "Could not save the PayPal payout email."),
			);
		} finally {
			setPayoutEmailSaving(false);
		}
	};

	const createCampaignLink = async () => {
		if (!campaignLabel.trim()) {
			setError("Please add a campaign label.");
			return;
		}
		setCampaignSaving(true);
		setCampaignMessage("");
		setPayoutMessage("");
		setPayoutEmailMessage("");
		setSupportRequestMessage("");
		setError("");
		try {
			const response = await apiServices.post("/affiliate/campaign-links", {
				label: campaignLabel,
				source: campaignSource,
			});
			setAffiliate(response.data);
			setCampaignLabel("");
			setCampaignMessage("Campaign link created.");
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not create campaign link."));
		} finally {
			setCampaignSaving(false);
		}
	};

	const requestCustomCode = async () => {
		if (!customCode.trim()) {
			setError("Please add the code you want.");
			return;
		}
		setCampaignSaving(true);
		setCampaignMessage("");
		setPayoutMessage("");
		setPayoutEmailMessage("");
		setSupportRequestMessage("");
		setError("");
		try {
			const response = await apiServices.post(
				"/affiliate/custom-code-request",
				{
					code: customCode,
				},
			);
			setAffiliate(response.data);
			setCustomCode("");
			const request = response.data?.customCodeRequest;
			setCampaignMessage(
				request?.status === "needs_review"
					? "Code request sent for review."
					: "Personal code saved.",
			);
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not request that code."));
		} finally {
			setCampaignSaving(false);
		}
	};

	const buildSupportIssue = (details: AffiliateSupportDetails) =>
		[
			`Support type: ${supportSubject}`,
			`Priority: ${details.priority}`,
			details.campaign ? `Campaign/code/link: ${details.campaign}` : "",
			`Preferred reply: ${details.contactPreference}`,
			details.attachmentName ? `Attachment: ${details.attachmentName}` : "",
			"",
			supportMessage.trim(),
		]
			.filter(Boolean)
			.join("\n");

	const sendSupportRequest = async (details: AffiliateSupportDetails) => {
		if (!supportSubject.trim() || supportMessage.trim().length < 10) {
			setError("Please add a subject and a short message.");
			return;
		}
		setSupportSaving(true);
		setSupportRequestMessage("");
		setPayoutMessage("");
		setPayoutEmailMessage("");
		setCampaignMessage("");
		setError("");
		try {
			const issue = buildSupportIssue(details);
			const ticketForm = new FormData();
			ticketForm.append("title", `Affiliate Support: ${supportSubject}`);
			ticketForm.append("issue", issue);
			ticketForm.append("type", "ticket");
			if (details.attachmentFile) {
				ticketForm.append("file", details.attachmentFile);
			}
			await apiServices.post("/profile/user-ticket", ticketForm, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const response = await apiServices.post("/affiliate/support-requests", {
				subject: supportSubject,
				message: issue,
			});
			setAffiliate(response.data);
			setSupportSubject("");
			setSupportMessage("");
			setSupportRequestMessage("Support request sent.");
		} catch (err) {
			setError(getApiErrorMessage(err, "Could not send support request."));
		} finally {
			setSupportSaving(false);
		}
	};

	const updateForm = (field: keyof JoinForm, value: string | boolean) => {
		setForm((current) => ({ ...current, [field]: value }));
	};

	const copyLink = async () => {
		if (!affiliate?.link) return;
		await navigator.clipboard.writeText(affiliate.link);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1800);
	};

	const stats = affiliate?.stats || emptyStats;
	const rates = affiliate?.settings?.commissionRates || [50, 30, 10];
	const journey = affiliate?.referralJourney || {
		clickedLink: stats.clicks,
		signedUp: stats.signups,
		subscribed: stats.paidUsers,
		eligibleCommission: 0,
	};
	const payoutProgress = affiliate?.minimumPayoutProgress || {
		minimumAmount: affiliate?.settings?.minimumPayoutAmount || 100,
		payableAmount: stats.payableEarnings,
		percent: 0,
		remainingAmount: affiliate?.settings?.minimumPayoutAmount || 100,
	};
	const marketingKit = affiliate?.marketingKit || {
		banners: affiliate?.creatives || [],
		captions: [],
		emailCopy: [],
		disclosureCopy: affiliate?.disclosureText ? [affiliate.disclosureText] : [],
		videoScripts: [],
		videoAds: [],
		postingRules: [],
	};
	const performanceRows = affiliate?.performance?.daily || [];
	const latestWeek = latestPoint(affiliate?.performance?.weekly || []);
	const latestMonth = latestPoint(affiliate?.performance?.monthly || []);
	const showUnavailableNotice =
		!loading && !affiliate?.joined && !canJoinFromMemberCountry;
	const dashboardBasePath = location.pathname.startsWith("/settings/affiliate")
		? "/settings/affiliate"
		: location.pathname.startsWith("/affiliate-app")
			? "/affiliate-app"
		: "/affiliate";

	if (affiliate?.joined) {
		return (
			<AffiliateDashboardWorkspace
				data={affiliate}
				section={normalizeAffiliateSection(section)}
				basePath={dashboardBasePath}
				contextLabel="Member affiliate dashboard"
				loading={loading}
				error={error}
				message={
					payoutEmailMessage ||
					campaignMessage ||
					supportRequestMessage ||
					payoutMessage
				}
				payoutPayPalEmail={form.payoutPayPalEmail}
				campaignLabel={campaignLabel}
				campaignSource={campaignSource}
				customCode={customCode}
				supportSubject={supportSubject}
				supportMessage={supportMessage}
				saving={
					payoutEmailSaving || campaignSaving || supportSaving || payoutLoading
				}
				onPayoutPayPalEmailChange={(value) =>
					updateForm("payoutPayPalEmail", value)
				}
				onCampaignLabelChange={setCampaignLabel}
				onCampaignSourceChange={setCampaignSource}
				onCustomCodeChange={setCustomCode}
				onSupportSubjectChange={setSupportSubject}
					onSupportMessageChange={setSupportMessage}
					onSavePayoutEmail={savePayoutEmail}
					onStartPayoutSetup={startPayoutSetup}
					onCreateCampaignLink={createCampaignLink}
					onRequestCustomCode={requestCustomCode}
					onSendSupportRequest={sendSupportRequest}
					supportAttachmentsEnabled
				/>
			);
		}

	return (
		<Container className="min-h-svh overflow-x-hidden bg-[#0d1015] text-slate-100 max-md:overflow-y-auto">
			<div className="flex min-h-svh flex-col lg:flex-row">
				<aside className="border-b border-slate-800 bg-[#14181d] p-4 lg:min-h-svh lg:w-[280px] lg:border-b-0 lg:border-r">
					<div className="text-lg font-semibold text-slate-50">
						Unjabbed Affiliates
					</div>
					<div className="mt-1 text-xs text-slate-500">
						Member partner dashboard
					</div>
					<nav className="mt-6 grid gap-1">
						{affiliateNavItems.map(([id, label]) => (
							<a
								key={id}
								href={`#${id}`}
								className="border border-transparent px-3 py-2 text-sm font-semibold text-slate-400 transition hover:border-pink-400/40 hover:bg-pink-500/10 hover:text-pink-100"
							>
								{label}
							</a>
						))}
					</nav>
				</aside>
				<main className="min-w-0 flex-1 p-4 lg:p-6">
					<div className="mx-auto grid max-w-7xl gap-5">
						<div className="border border-slate-800 bg-[#12161c] p-5 lg:p-6">
							<div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
								<div>
									<div className="text-xs font-semibold uppercase tracking-normal text-pink-300">
										Affiliate program
									</div>
									<h1 className="mt-2 text-3xl font-semibold text-slate-50">
										{affiliate?.joined
											? affiliate.profile?.displayName ||
												"Your affiliate workspace"
											: "Apply to become an affiliate"}
									</h1>
									<p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">
										Share Unjabbed responsibly, track referrals, manage payouts,
										and use approved creative assets from one polished
										workspace.
									</p>
								</div>
								<div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
									<MiniStat
										label="Status"
										value={
											affiliate?.joined
												? affiliate.status || "Joined"
												: "Not joined"
										}
									/>
									<MiniStat label="Code" value={affiliate?.code || "Pending"} />
									<MiniStat
										label="Conversion"
										value={`${conversionRate(stats)}%`}
									/>
									<MiniStat
										label="Payable"
										value={money(stats.payableEarnings)}
									/>
								</div>
							</div>
						</div>

						{error && (
							<Container className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-red-100">
								<TextComponent variant="h6">{error}</TextComponent>
							</Container>
						)}

						<Container
							id="overview"
							className="border border-slate-800 bg-slate-900 p-5 shadow-sm"
						>
							<Column className="gap-3">
								<TextComponent variant="h5" className="font-semibold">
									{affiliate?.joined
										? "Your affiliate link"
										: "Join the affiliate program"}
								</TextComponent>
								<TextComponent variant="h6">
									Earn {rates[0]}%, then {rates[1]}%, then {rates[2]}% on the
									first three eligible paid subscription payments from each
									referred member.
								</TextComponent>
								{!affiliate?.joined && (
									<TextComponent variant="h6">
										Complete this form after you create or sign in to your
										Unjabbed account. Once accepted, your affiliate link and
										dashboard will appear here.
									</TextComponent>
								)}
								<TextComponent variant="h6">
									Payouts require accepted rules, the safety waiting period, the
									minimum payout amount, and a confirmed PayPal payout email.
								</TextComponent>

								{loading ? (
									<TextComponent variant="h6">Loading...</TextComponent>
								) : showUnavailableNotice ? (
									<Container className="rounded-md border border-slate-800 bg-slate-800 p-3 text-slate-300">
										<TextComponent variant="h6">
											The affiliate program is not available in your country
											yet.
										</TextComponent>
									</Container>
								) : affiliate?.joined && affiliate?.link ? (
									<>
										<Container className="border border-pink-400/30 bg-pink-500/10 p-4 break-all font-mono text-sm text-pink-200">
											<TextComponent variant="h6">
												{affiliate.link}
											</TextComponent>
										</Container>
										<div className="flex flex-col gap-2 sm:flex-row">
											<CustomButton small onClick={copyLink}>
												{copied ? "Copied" : "Copy link"}
											</CustomButton>
											<CustomButton
												small
												color="white"
												onClick={() => {
													if (navigator.share) {
														navigator.share({
															title: "Unjabbed",
															text: affiliate.disclosureText,
															url: affiliate.link,
														});
													}
												}}
											>
												Share
											</CustomButton>
										</div>
									</>
								) : (
									<Column id="application" className="gap-3">
										<div className="grid gap-3 md:grid-cols-2">
											<input
												className="border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.fullName}
												onChange={(event) =>
													updateForm("fullName", event.target.value)
												}
												placeholder="Full legal name"
											/>
											<input
												className="border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.businessName}
												onChange={(event) =>
													updateForm("businessName", event.target.value)
												}
												placeholder="Business or brand name"
											/>
											<input
												className="border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.displayName}
												onChange={(event) =>
													updateForm("displayName", event.target.value)
												}
												placeholder="Affiliate display name"
											/>
											<input
												className="border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.contactEmail}
												onChange={(event) =>
													updateForm("contactEmail", event.target.value)
												}
												placeholder="Contact email"
											/>
											<select
												className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.country}
												onChange={(event) =>
													updateForm("country", event.target.value)
												}
											>
												<option value="">Select country</option>
												{affiliateAllowedCountries.map((country) => (
													<option key={country} value={country}>
														{country}
													</option>
												))}
											</select>
											<select
												className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.taxCountry}
												onChange={(event) =>
													updateForm("taxCountry", event.target.value)
												}
											>
												<option value="">Select tax country</option>
												{affiliateAllowedCountries.map((country) => (
													<option key={country} value={country}>
														{country}
													</option>
												))}
											</select>
											<input
												className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.websiteUrl}
												onChange={(event) =>
													updateForm("websiteUrl", event.target.value)
												}
												placeholder="Website"
											/>
											<input
												className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.primaryChannelUrl}
												onChange={(event) =>
													updateForm("primaryChannelUrl", event.target.value)
												}
												placeholder="Main social, newsletter, podcast, or community link"
											/>
											<select
												className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.audienceSizeRange}
												onChange={(event) =>
													updateForm("audienceSizeRange", event.target.value)
												}
											>
												<option value="">Audience size</option>
												{audienceSizeOptions.map((option) => (
													<option key={option} value={option}>
														{option}
													</option>
												))}
											</select>
											<select
												className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.referralSource}
												onChange={(event) =>
													updateForm("referralSource", event.target.value)
												}
											>
												<option value="">How did you hear about us?</option>
												{referralSourceOptions.map((source) => (
													<option key={source} value={source}>
														{source}
													</option>
												))}
											</select>
											{form.referralSource === "Other" && (
												<input
													className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
													value={form.referralSourceOther}
													onChange={(event) =>
														updateForm(
															"referralSourceOther",
															event.target.value,
														)
													}
													placeholder="Where did you hear about us?"
												/>
											)}
											<input
												className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
												value={form.payoutPayPalEmail}
												onChange={(event) =>
													updateForm("payoutPayPalEmail", event.target.value)
												}
												placeholder="PayPal payout email"
											/>
										</div>
										<textarea
											className="min-h-[120px] rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
											value={form.promotionPlan}
											onChange={(event) =>
												updateForm("promotionPlan", event.target.value)
											}
											placeholder="How will you promote Unjabbed?"
										/>
										<TextComponent variant="h6">
											Bank details are not collected by Unjabbed. Affiliates add
											a confirmed PayPal payout email, and payouts stay held
											until PayPal Payouts access, safety checks, and payout
											rules are satisfied.
										</TextComponent>
										<Container className="rounded-md border border-slate-800 bg-slate-800 p-3 text-sm leading-relaxed text-slate-300">
											Before you submit, you can read the{" "}
											<a
												className="font-semibold text-pink-300 underline"
												href="https://unjabbed.app/affiliate-program-rules/"
												target="_blank"
												rel="noreferrer"
											>
												Affiliate Program Rules
											</a>
											,{" "}
											<a
												className="font-semibold text-pink-300 underline"
												href="https://unjabbed.app/privacy-policy/"
												target="_blank"
												rel="noreferrer"
											>
												Privacy Policy
											</a>
											, and{" "}
											<a
												className="font-semibold text-pink-300 underline"
												href="https://unjabbed.app/terms-and-conditions/"
												target="_blank"
												rel="noreferrer"
											>
												Terms &amp; Conditions
											</a>
											. When you continue, you will need to scroll through and
											accept the affiliate agreement before the application is
											submitted.
										</Container>
										<CustomButton loading={joining} onClick={openAgreement}>
											Review agreement and join
										</CustomButton>
									</Column>
								)}
							</Column>
						</Container>

						{affiliate?.joined && (
							<Container
								id="performance"
								className="border border-slate-800 bg-slate-900 p-5 shadow-sm"
							>
								<Column className="gap-3">
									<TextComponent variant="h5" className="font-semibold">
										Earnings and performance
									</TextComponent>
									<TextComponent variant="h6" className="text-slate-400">
										Pending, approved, blocked, paid, payout progress, and
										activity.
									</TextComponent>
									<div className="grid grid-cols-2 gap-3 md:grid-cols-4">
										<MiniStat
											label="Pending"
											value={money(stats.pendingEarnings)}
										/>
										<MiniStat
											label="Approved"
											value={money(stats.payableEarnings)}
										/>
										<MiniStat
											label="Blocked"
											value={money(stats.blockedEarnings)}
										/>
										<MiniStat label="Paid" value={money(stats.paidEarnings)} />
									</div>
									<div className="grid gap-3 md:grid-cols-2">
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<TextComponent variant="h6" className="font-semibold">
												Next payout date
											</TextComponent>
											<TextComponent variant="h6">
												{dateText(affiliate.nextPayoutDate)}
											</TextComponent>
										</Container>
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<div className="flex items-center justify-between gap-3">
												<TextComponent variant="h6" className="font-semibold">
													Minimum payout
												</TextComponent>
												<TextComponent variant="h6">
													{payoutProgress.percent}%
												</TextComponent>
											</div>
											<div className="mt-3">
												<ProgressBar percent={payoutProgress.percent} />
											</div>
											<TextComponent
												variant="h6"
												className="mt-2 text-slate-400"
											>
												{money(payoutProgress.payableAmount)} of{" "}
												{money(payoutProgress.minimumAmount)}
											</TextComponent>
										</Container>
									</div>
									<div id="journey" className="grid gap-3 md:grid-cols-4">
										{[
											["Clicked link", journey.clickedLink],
											["Signed up", journey.signedUp],
											["Subscribed", journey.subscribed],
											["Eligible commission", journey.eligibleCommission],
										].map(([label, value], index) => (
											<Container
												key={label}
												className="border border-slate-800 bg-slate-950 p-4"
											>
												<div className="mb-2 grid h-7 w-7 place-items-center bg-pink-400 text-sm font-bold text-slate-950">
													{index + 1}
												</div>
												<TextComponent variant="h6" className="font-semibold">
													{label}
												</TextComponent>
												<TextComponent variant="h6" className="text-slate-400">
													{numberText(Number(value))}
												</TextComponent>
											</Container>
										))}
									</div>
									<Container className="border border-slate-800 bg-slate-950 p-4">
										<TextComponent variant="h6" className="font-semibold">
											Clicks by day
										</TextComponent>
										<div className="mt-4">
											<SparkBars rows={performanceRows} />
										</div>
									</Container>
									<div className="grid gap-3 md:grid-cols-2">
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<TextComponent variant="h6" className="font-semibold">
												Latest week
											</TextComponent>
											<div className="mt-2 grid grid-cols-2 gap-2 text-sm text-slate-400">
												<div>{numberText(latestWeek.clicks)} clicks</div>
												<div>{numberText(latestWeek.signups)} signups</div>
												<div>{numberText(latestWeek.paidUsers)} paid</div>
												<div>{money(latestWeek.earnings)}</div>
											</div>
										</Container>
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<TextComponent variant="h6" className="font-semibold">
												Latest month
											</TextComponent>
											<div className="mt-2 grid grid-cols-2 gap-2 text-sm text-slate-400">
												<div>{numberText(latestMonth.clicks)} clicks</div>
												<div>{numberText(latestMonth.signups)} signups</div>
												<div>{numberText(latestMonth.paidUsers)} paid</div>
												<div>{money(latestMonth.earnings)}</div>
											</div>
										</Container>
									</div>
									<div className="grid grid-cols-2 gap-3 md:grid-cols-4">
										{statItems(stats).map(([label, value]) => (
											<MiniStat key={label} label={label} value={value} />
										))}
									</div>
									<div id="payouts" className="pt-2" />
									<TextComponent variant="h6">
										Payout setup:{" "}
										{affiliate?.payoutSetupStatus || "not started"}
									</TextComponent>
									<TextComponent variant="h6">
										{affiliate?.payoutSetupMessage}
									</TextComponent>
									<div className="grid gap-2 md:grid-cols-[1fr_auto]">
										<input
											className="rounded-md border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
											value={form.payoutPayPalEmail}
											onChange={(event) =>
												updateForm("payoutPayPalEmail", event.target.value)
											}
											placeholder="PayPal payout email"
										/>
										<CustomButton
											loading={payoutEmailSaving}
											onClick={savePayoutEmail}
										>
											Save PayPal email
										</CustomButton>
									</div>
									<TextComponent variant="h6" className="text-slate-400">
										Unjabbed does not collect bank details. Payouts stay held
										until all safety checks pass.
									</TextComponent>
									{payoutEmailMessage && (
										<Container className="rounded-md border border-slate-800 bg-slate-800 p-3">
											<TextComponent variant="h6">
												{payoutEmailMessage}
											</TextComponent>
										</Container>
									)}
									<CustomButton
										loading={payoutLoading}
										onClick={startPayoutSetup}
									>
										Start payout setup
									</CustomButton>
									{payoutMessage && (
										<Container className="rounded-md border border-slate-800 bg-slate-800 p-3">
											<TextComponent variant="h6">
												{payoutMessage}
											</TextComponent>
										</Container>
									)}
								</Column>
							</Container>
						)}

						{affiliate?.joined && (
							<Container
								id="links"
								className="border border-slate-800 bg-slate-900 p-5 shadow-sm"
							>
								<Column className="gap-3">
									<TextComponent variant="h5" className="font-semibold">
										Campaign links and traffic sources
									</TextComponent>
									<TextComponent variant="h6" className="text-slate-400">
										Create labelled links for each video, post, email, website,
										or community mention.
									</TextComponent>
									<div className="grid gap-2 md:grid-cols-[1fr_150px_auto]">
										<input
											className="border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
											value={campaignLabel}
											onChange={(event) => setCampaignLabel(event.target.value)}
											placeholder="Campaign label"
										/>
										<select
											className="border border-slate-700 bg-slate-950 p-3 text-slate-100"
											value={campaignSource}
											onChange={(event) =>
												setCampaignSource(event.target.value)
											}
										>
											{campaignSourceOptions.map((source) => (
												<option key={source} value={source}>
													{source}
												</option>
											))}
										</select>
										<CustomButton
											loading={campaignSaving}
											onClick={createCampaignLink}
										>
											Create
										</CustomButton>
									</div>
									{campaignMessage && (
										<Container className="rounded-md border border-slate-800 bg-slate-800 p-3">
											<TextComponent variant="h6">
												{campaignMessage}
											</TextComponent>
										</Container>
									)}
									<div className="grid gap-3 lg:grid-cols-2">
										{(affiliate.campaignLinks || []).length === 0 ? (
											<Container className="border border-slate-800 bg-slate-950 p-4">
												<TextComponent variant="h6" className="text-slate-500">
													No custom campaign links yet.
												</TextComponent>
											</Container>
										) : (
											affiliate.campaignLinks.map((link) => (
												<Container
													key={link.id}
													className="border border-slate-800 bg-slate-950 p-4"
												>
													<div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
														<div>
															<TextComponent
																variant="h6"
																className="font-semibold"
															>
																{link.label}
															</TextComponent>
															<TextComponent
																variant="h6"
																className="text-slate-500"
															>
																{cleanLabel(link.source)} ·{" "}
																{numberText(link.clicks)} clicks
															</TextComponent>
														</div>
														<CustomButton
															small
															onClick={() =>
																navigator.clipboard.writeText(link.link)
															}
														>
															Copy
														</CustomButton>
													</div>
													<div className="mt-3 break-all font-mono text-xs text-slate-400">
														{link.link}
													</div>
												</Container>
											))
										)}
									</div>
									<div className="overflow-x-auto">
										<table className="w-full min-w-[560px] text-left text-sm">
											<thead className="text-xs uppercase tracking-normal text-slate-500">
												<tr>
													<th className="border-b border-slate-800 py-2">
														Source
													</th>
													<th className="border-b border-slate-800 py-2">
														Clicks
													</th>
													<th className="border-b border-slate-800 py-2">
														Signups
													</th>
													<th className="border-b border-slate-800 py-2">
														Paid
													</th>
													<th className="border-b border-slate-800 py-2">
														Earnings
													</th>
												</tr>
											</thead>
											<tbody>
												{(affiliate.trafficSources || []).length === 0 ? (
													<tr>
														<td className="py-4 text-slate-500" colSpan={5}>
															No traffic sources tracked yet.
														</td>
													</tr>
												) : (
													affiliate.trafficSources.map((source) => (
														<tr key={source.source}>
															<td className="border-b border-slate-800 py-3 text-slate-100">
																{cleanLabel(source.source)}
															</td>
															<td className="border-b border-slate-800 py-3 text-slate-300">
																{numberText(source.clicks)}
															</td>
															<td className="border-b border-slate-800 py-3 text-slate-300">
																{numberText(source.signups)}
															</td>
															<td className="border-b border-slate-800 py-3 text-slate-300">
																{numberText(source.paidUsers)}
															</td>
															<td className="border-b border-slate-800 py-3 text-slate-300">
																{money(source.earnings)}
															</td>
														</tr>
													))
												)}
											</tbody>
										</table>
									</div>
								</Column>
							</Container>
						)}

						{affiliate?.joined && (
							<Container
								id="kit"
								className="border border-slate-800 bg-slate-900 p-5 shadow-sm"
							>
								<Column className="gap-3">
									<TextComponent variant="h5" className="font-semibold">
										Approved marketing kit
									</TextComponent>
									<TextComponent variant="h6" className="text-slate-400">
										Approved banners, captions, email copy, disclosure copy,
										short video scripts, and posting rules.
									</TextComponent>
									{(marketingKit.banners || []).length === 0 ? (
										<TextComponent variant="h6">
											No banners are available yet.
										</TextComponent>
									) : (
										<div className="grid gap-3 lg:grid-cols-2">
											{(marketingKit.banners || []).map((creative) => (
												<Container
													key={creative.id}
													className="border border-slate-800 bg-slate-950 p-4"
												>
													<Column className="gap-2">
														{creative.previewUrl && (
															<img
																src={creative.previewUrl}
																alt={creative.title}
																className="aspect-[16/7] w-full border border-slate-800 object-cover"
															/>
														)}
														<TextComponent
															variant="h6"
															className="font-semibold"
														>
															{creative.title}
														</TextComponent>
														<TextComponent variant="h6">
															{creative.dimensions} · {creative.format}
														</TextComponent>
														<TextComponent
															variant="h6"
															className="text-slate-400"
														>
															{creative.notes}
														</TextComponent>
														<CustomButton
															small
															onClick={() =>
																window.open(
																	creative.downloadUrl,
																	"_blank",
																	"noopener,noreferrer",
																)
															}
														>
															Open banners
														</CustomButton>
													</Column>
												</Container>
											))}
										</div>
									)}
									<div className="grid gap-3 lg:grid-cols-2">
										{[
											["Captions", marketingKit.captions],
											["Email copy", marketingKit.emailCopy],
											["Disclosure copy", marketingKit.disclosureCopy],
											["Short video scripts", marketingKit.videoScripts],
											["Posting rules", marketingKit.postingRules],
										].map(([title, rows]) => (
											<Container
												key={String(title)}
												className="border border-slate-800 bg-slate-950 p-4"
											>
												<TextComponent variant="h6" className="font-semibold">
													{title}
												</TextComponent>
												<Column className="mt-2 gap-2">
													{(rows as string[]).slice(0, 3).map((row) => (
														<TextComponent
															key={row}
															variant="h6"
															className="text-slate-400"
														>
															{row}
														</TextComponent>
													))}
												</Column>
											</Container>
										))}
									</div>
								</Column>
							</Container>
						)}

						{affiliate?.joined && (
							<Container
								id="rules"
								className="border border-slate-800 bg-slate-900 p-5 shadow-sm"
							>
								<Column className="gap-3">
									<TextComponent variant="h5" className="font-semibold">
										Payout history, rules, and updates
									</TextComponent>
									<div className="grid gap-3 lg:grid-cols-2">
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<TextComponent variant="h6" className="font-semibold">
												Payout history
											</TextComponent>
											<Column className="mt-3 gap-2">
												{(affiliate.payoutHistory || []).length === 0 ? (
													<TextComponent
														variant="h6"
														className="text-slate-500"
													>
														No payouts have been sent yet.
													</TextComponent>
												) : (
													affiliate.payoutHistory.map((payout) => (
														<Container
															key={payout.id}
															className="border border-slate-800 bg-slate-900 p-3"
														>
															<TextComponent
																variant="h6"
																className="font-semibold"
															>
																{money(payout.amount)}
															</TextComponent>
															<TextComponent
																variant="h6"
																className="text-slate-400"
															>
																{cleanLabel(payout.status)} ·{" "}
																{cleanLabel(payout.provider)} ·{" "}
																{dateText(payout.paidAt || payout.createdAt)}
															</TextComponent>
															{payout.failureReason && (
																<TextComponent
																	variant="h6"
																	className="text-amber-200"
																>
																	{payout.failureReason}
																</TextComponent>
															)}
														</Container>
													))
												)}
											</Column>
										</Container>
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<TextComponent variant="h6" className="font-semibold">
												Tier progress
											</TextComponent>
											<TextComponent
												variant="h5"
												className="mt-2 font-semibold"
											>
												{affiliate.tierProgress?.currentTier || "Starter"}
											</TextComponent>
											<div className="mt-4 flex items-center justify-between gap-3">
												<TextComponent variant="h6" className="text-slate-400">
													Next: {affiliate.tierProgress?.nextTier || "Top tier"}
												</TextComponent>
												<TextComponent variant="h6">
													{affiliate.tierProgress?.percent || 0}%
												</TextComponent>
											</div>
											<div className="mt-2">
												<ProgressBar
													percent={affiliate.tierProgress?.percent || 0}
												/>
											</div>
											<TextComponent
												variant="h6"
												className="mt-2 text-slate-500"
											>
												{affiliate.tierProgress?.paidUsers || 0} of{" "}
												{affiliate.tierProgress?.targetPaidUsers || 1} paid
												members
											</TextComponent>
										</Container>
									</div>
									<div className="grid gap-3 lg:grid-cols-2">
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<TextComponent variant="h6" className="font-semibold">
												Commission rules
											</TextComponent>
											<Column className="mt-3 gap-3">
												{(affiliate.commissionRules || []).map((rule) => (
													<div key={rule.title}>
														<TextComponent
															variant="h6"
															className="font-semibold"
														>
															{rule.title}
														</TextComponent>
														<TextComponent
															variant="h6"
															className="text-slate-400"
														>
															{rule.description}
														</TextComponent>
													</div>
												))}
											</Column>
										</Container>
										<Container className="border border-slate-800 bg-slate-950 p-4">
											<TextComponent variant="h6" className="font-semibold">
												Notifications
											</TextComponent>
											<Column className="mt-3 gap-2">
												{(affiliate.notifications || []).length === 0 ? (
													<TextComponent
														variant="h6"
														className="text-slate-500"
													>
														No affiliate notifications yet.
													</TextComponent>
												) : (
													affiliate.notifications.map((notice) => (
														<Container
															key={notice.id}
															className="border border-slate-800 bg-slate-900 p-3"
														>
															<TextComponent
																variant="h6"
																className="font-semibold"
															>
																{notice.title}
															</TextComponent>
															<TextComponent
																variant="h6"
																className="text-slate-400"
															>
																{notice.message}
															</TextComponent>
															<TextComponent
																variant="h6"
																className="text-slate-500"
															>
																{dateText(notice.createdAt)}
															</TextComponent>
														</Container>
													))
												)}
											</Column>
										</Container>
									</div>
								</Column>
							</Container>
						)}

						{affiliate?.joined && (
							<Container
								id="support"
								className="border border-slate-800 bg-slate-900 p-5 shadow-sm"
							>
								<Column className="gap-3">
									<TextComponent variant="h5" className="font-semibold">
										Support and custom creative
									</TextComponent>
									<TextComponent variant="h6" className="text-slate-400">
										Ask a question or request custom approved creative.
									</TextComponent>
									<div className="grid gap-3 md:grid-cols-[0.8fr_1.2fr_auto]">
										<input
											className="border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
											value={supportSubject}
											onChange={(event) =>
												setSupportSubject(event.target.value)
											}
											placeholder="Subject"
										/>
										<textarea
											className="min-h-[48px] border border-slate-700 bg-slate-950 p-3 text-slate-100 placeholder:text-slate-500"
											value={supportMessage}
											onChange={(event) =>
												setSupportMessage(event.target.value)
											}
											placeholder="Question or custom creative request"
											/>
											<CustomButton
												loading={supportSaving}
												onClick={() =>
													sendSupportRequest({
														priority: "Normal",
														campaign: "",
														contactPreference: "Reply by email",
														attachmentFile: null,
														attachmentName: "",
													})
												}
											>
											Send
										</CustomButton>
									</div>
									{supportRequestMessage && (
										<Container className="rounded-md border border-slate-800 bg-slate-800 p-3">
											<TextComponent variant="h6">
												{supportRequestMessage}
											</TextComponent>
										</Container>
									)}
								</Column>
							</Container>
						)}

						<Container
							id="disclosure"
							className="border border-slate-800 bg-slate-900 p-5 shadow-sm"
						>
							<Column className="gap-2">
								<TextComponent variant="h5" className="font-semibold">
									Disclosure text
								</TextComponent>
								<TextComponent variant="h6">
									{affiliate?.disclosureText ||
										"I may earn a commission if you sign up or subscribe through my Unjabbed link."}
								</TextComponent>
							</Column>
						</Container>
					</div>
				</main>
			</div>
			{showAgreement && (
				<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/55 px-4 py-6">
					<div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl">
						<div className="border-b border-slate-800 p-4">
							<TextComponent variant="h5" className="font-semibold">
								Affiliate agreement
							</TextComponent>
							<TextComponent variant="h6" className="text-slate-400">
								Scroll to the bottom before you can agree and submit.
							</TextComponent>
						</div>
						<div
							ref={agreementRef}
							onScroll={handleAgreementScroll}
							className="max-h-[52vh] overflow-y-auto p-4 text-sm leading-relaxed text-slate-300"
						>
							<p>
								By submitting this affiliate application, you are applying to
								join the Unjabbed affiliate program and agree to the Affiliate
								Program Rules, Privacy Policy, and Terms &amp; Conditions.
							</p>
							<ul className="my-4 list-disc space-y-3 pl-5">
								{agreementItems.map((item) => (
									<li key={item}>{item}</li>
								))}
							</ul>
							<p>
								Read the full{" "}
								<a
									className="font-semibold text-pink-300 underline"
									href="https://unjabbed.app/affiliate-program-rules/"
									target="_blank"
									rel="noreferrer"
								>
									Affiliate Program Rules
								</a>
								,{" "}
								<a
									className="font-semibold text-pink-300 underline"
									href="https://unjabbed.app/privacy-policy/"
									target="_blank"
									rel="noreferrer"
								>
									Privacy Policy
								</a>
								, and{" "}
								<a
									className="font-semibold text-pink-300 underline"
									href="https://unjabbed.app/terms-and-conditions/"
									target="_blank"
									rel="noreferrer"
								>
									Terms &amp; Conditions
								</a>
								.
							</p>
						</div>
						<div className="flex flex-col gap-2 border-t border-slate-800 p-4 sm:flex-row sm:justify-end">
							<CustomButton
								color="white"
								onClick={() => setShowAgreement(false)}
								disabled={joining}
							>
								Cancel
							</CustomButton>
							<CustomButton
								onClick={joinProgram}
								loading={joining}
								disabled={!agreementScrolled}
							>
								Agree and submit
							</CustomButton>
						</div>
					</div>
				</div>
			)}
		</Container>
	);
};
